// Service name constants to avoid hardcoded strings
export const SERVICE_NAMES = {
    ACCOUNT: 'ACCOUNT',
    ACCOUNT_USER: 'ACCOUNT_USER',
    USER: 'USER',
    TRANSACTION: 'TRANSACTION',
    DOCUMENT: 'DOCUMENT',
    SIGNING: 'SIGNING',
    ATTACHMENT: 'ATTACHMENT',
    ATTACHMENT_UPLOAD: 'ATTACHMENT_UPLOAD',
    FIELD: 'FIELD',
    APPROVAL: 'APPROVAL',
    AUTHORIZATION_TOKEN: 'AUTHORIZATION_TOKEN',
    CONFIGURATION: 'CONFIGURATION',
    FEATURE: 'FEATURE',
    SUBSCRIPTION: 'SUBSCRIPTION',
    PERMISSION: 'PERMISSION',
    REGISTRATION: 'REGISTRATION',
    SIGNATURE: 'SIGNATURE',
    RECIPIENT: 'RECIPIENT',
    CONTACT: 'CONTACT',
    DOCUMENT_VISIBILITY: 'DOCUMENT_VISIBILITY',
    BRAND: 'BRAND',
    CALLBACK: 'CALLBACK',
    TWO_FACTOR: 'TWO_FACTOR',
    ESV: 'ESV',
    LAYOUT: 'LAYOUT',
    DELEGATION: 'DELEGATION',
    CONDITIONAL_FIELD: 'CONDITIONAL_FIELD',
    BULK: 'BULK',
    AUDIT: 'AUDIT',
    REMINDER: 'REMINDER',
    CUSTOM_FIELD: 'CUSTOM_FIELD',
    GROUP: 'GROUP',
    RESOURCE: 'RESOURCE',
    ARCHIVE: 'ARCHIVE'
} as const;

// Proxy prefixes for different microservices
export const PROXY_PREFIXES = {
    [SERVICE_NAMES.ACCOUNT]: '/proxyAccount',
    [SERVICE_NAMES.ACCOUNT_USER]: '/proxyAccountUser',
    [SERVICE_NAMES.USER]: '/proxyUser',
    [SERVICE_NAMES.TRANSACTION]: '/proxyTransaction',
    [SERVICE_NAMES.DOCUMENT]: '/proxyDocument',
    [SERVICE_NAMES.SIGNING]: '/proxySigning',
    [SERVICE_NAMES.ATTACHMENT]: '/proxyAttachment',
    [SERVICE_NAMES.ATTACHMENT_UPLOAD]: '/proxyAttachmentUpload',
    [SERVICE_NAMES.FIELD]: '/proxyField',
    [SERVICE_NAMES.APPROVAL]: '/proxyApproval',
    [SERVICE_NAMES.AUTHORIZATION_TOKEN]: '/proxyAuthorizationToken',
    [SERVICE_NAMES.CONFIGURATION]: '/proxyConfiguration',
    [SERVICE_NAMES.FEATURE]: '/proxyFeature',
    [SERVICE_NAMES.SUBSCRIPTION]: '/proxySubscription',
    [SERVICE_NAMES.PERMISSION]: '/proxyPermission',
    [SERVICE_NAMES.REGISTRATION]: '/proxyRegistration',
    [SERVICE_NAMES.SIGNATURE]: '/proxySignature',
    [SERVICE_NAMES.RECIPIENT]: '/proxyRecipient',
    [SERVICE_NAMES.CONTACT]: '/proxyContact',
    [SERVICE_NAMES.DOCUMENT_VISIBILITY]: '/proxyDocumentVisibility',
    [SERVICE_NAMES.BRAND]: '/proxyBrand',
    [SERVICE_NAMES.CALLBACK]: '/proxyCallback',
    [SERVICE_NAMES.TWO_FACTOR]: '/proxyTwoFactor',
    [SERVICE_NAMES.ESV]: '/proxyEsv',
    [SERVICE_NAMES.LAYOUT]: '/proxyLayout',
    [SERVICE_NAMES.DELEGATION]: '/proxyDelegation',
    [SERVICE_NAMES.CONDITIONAL_FIELD]: '/proxyConditionalField',
    [SERVICE_NAMES.BULK]: '/proxyBulk',
    [SERVICE_NAMES.AUDIT]: '/proxyAudit',
    [SERVICE_NAMES.REMINDER]: '/proxyReminder',
    [SERVICE_NAMES.CUSTOM_FIELD]: '/proxyCustomField',
    [SERVICE_NAMES.GROUP]: '/proxyGroup',
    [SERVICE_NAMES.RESOURCE]: '/proxyResource',
    [SERVICE_NAMES.ARCHIVE]: '/proxyArchive'
} as const;

export type ServiceName = keyof typeof PROXY_PREFIXES;
export type ProxyPrefix = typeof PROXY_PREFIXES[ServiceName];

export const APPROVAL_TYPES = {
    SIGNATURE: 'signature',
    INITIALS: 'initials',
    CAPTURE: 'capture',
    ACCEPT: 'accept',
    PARAPH: 'paraph'
} as const;

// Field types from Swagger API documentation
export const FIELD_TYPES = {
    TEXTFIELD: 'textfield',
    TEXTAREA: 'textarea',
    PARAGRAPH: 'paragraph',
    LIST: 'list',
    RADIO: 'radio',
    CHECKBOX: 'checkbox',
    LABEL: 'label',
    DATE: 'date',
    CUSTOMFIELD: 'customfield'
} as const;

// Field bindings
export const FIELD_BINDINGS = {
    SIGNER_NAME: 'signer_name',
    SIGNER_TITLE: 'signer_title',
    SIGNER_COMPANY: 'signer_company',
    SIGNING_DATE: 'signing_date'
} as const;

export const RECIPIENT_PLACEHOLDERS = {
    OWNER: 'owner'
} as const;

export const RECIPIENT_STATUSES = {
    NONE: 'none',
    COMPLETED: 'completed',
    CURRENT_SIGNER: 'current_signer',
    FUTURE_SIGNER: 'future_signer',
    REVIEWER: 'reviewer',
    OWNER_FINISHER: 'owner_finisher'
} as const;

// Configuration types
export const CONFIGURATION_TYPES = {
    ACCOUNT_SETTINGS: 'account_settings' as const,
    TRANSACTION_SETTINGS: 'transaction_settings' as const,
    DESIGNER_SETTINGS: 'designer_settings' as const
} as const;

// ESV verification methods
export const ESV_VERIFICATION_METHODS = {
    CERTIFICATE: 'certificate',
    EXTERNAL: 'external',
    CLOUD_SIGNING: 'cloud_signing'
} as const;

export const ARCHIVE_REQUEST_STATUSES = {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed'
} as const;

/**
 * API endpoint paths organized by service.
 * Single source of truth for all endpoint paths.
 */
export const API_ENDPOINTS = {
    [SERVICE_NAMES.USER]: {
        FORGOT_PASSWORD: 'forgot-password',
        RESET_PASSWORD: 'users/:userId/reset-password',
        LIST: 'users',
        COUNT: 'users/count',
        BY_ID: 'users/:userId',
        BY_EMAIL: 'users/by-email',
        TOKEN_FEATURES_BY_ID: 'users/:userId/token-features'
    },
    [SERVICE_NAMES.ACCOUNT]: {
        LIST: 'accounts',
        BY_ID: 'accounts/:accountId',
        SETTINGS: 'accounts/settings'
    },
    [SERVICE_NAMES.CONFIGURATION]: {
        ACCOUNT_SETTINGS: 'settings/account',
        TRANSACTION_SETTINGS: 'settings/transaction',
        DESIGNER_SETTINGS: 'settings/designer',
        USER_SECURITY_POLICY_BY_ID: 'users/:userId/security-policy'
    },
    [SERVICE_NAMES.AUTHORIZATION_TOKEN]: {
        AUTHORIZE_LEGACY: 'authorize/legacy',
        AUTHORIZE_SSO: 'authorize/sso-auth'
    },
    [SERVICE_NAMES.REGISTRATION]: {
        VALIDATE_EMAIL: 'registrations/validate/email',
        VALIDATE_PHONE: 'registrations/validate/phone',
        CREATE: 'registrations',
        STATUS: 'registrations/:registrationId',
        VERIFY_EMAIL: 'registrations/:registrationId/verify-email',
        VERIFY_SMS: 'registrations/:registrationId/verify-sms',
        RESEND_SMS: 'registrations/:registrationId/resend-sms',
        COMPLETE: 'registrations/:registrationId/complete'
    },
    [SERVICE_NAMES.ACCOUNT_USER]: {
        REGISTRATION: 'accounts/:accountId/users/:userId/registrations/:registrationId',
        REGISTRATION_CONFIRM: 'accounts/:accountId/users/:userId/registrations/:registrationId/confirm',
        REGISTRATION_COMPLETE: 'accounts/:accountId/users/:userId/registrations/:registrationId/complete'
    },
    [SERVICE_NAMES.TWO_FACTOR]: {
        LOGIN: 'auth/login',
        VERIFY: 'auth/2fa/verify',
        STATUS: '2fa/status',
        SETUP: '2fa/setup',
        SETUP_VERIFY: '2fa/setup/verify',
        DISABLE: '2fa/disable',
        BACKUP_CODES: '2fa/backup-codes',
        RESET_REQUEST: 'auth/2fa/reset-request',
        DISMISS: '2fa/dismiss',
        DISABLE_FOR_USER: '2fa/users/:userId/disable'
    },
    [SERVICE_NAMES.FEATURE]: {
        ACCOUNT_FEATURES: 'accounts/:accountId/features'
    },
    [SERVICE_NAMES.BRAND]: {
        DEFAULT_BRAND: 'accounts/:accountId/default-brand'
    },
    [SERVICE_NAMES.BULK]: {
        BULK_STORE: 'accounts/:accountId/templates/:templateId/bulk',
        FAST_TRACK_URLS: 'accounts/:accountId/templates/:templateId/fast-track/urls',
        FAST_TRACK_SENDING: 'accounts/:accountId/templates/:templateId/fast-track/sending',
        FAST_TRACK_SIGNING: 'accounts/:accountId/templates/:templateId/fast-track/signing',
        FAST_TRACK_STORE: 'accounts/:accountId/templates/:templateId/fast-track'
    }
} as const;
